/**
 * 文件定位：
 * 1. 当前文件位于 cad-core/feature/delete 目录；
 * 2. 负责校验用户确认的删除计划与即将执行的删除计划是否一致；
 * 3. Viewer 在执行删除前调用该模块，避免确认框与实际删除范围发生竞态分叉。
 */

import type {FeatureDeletionFeatureSummary, FeatureDeletionPlan} from './FeatureDeletionTypes';

/**
 * 特征删除计划不一致错误消息。
 */
export const FEATURE_DELETION_PLAN_CHANGED_MESSAGE = '特征删除计划已变化，请重新确认。';

/**
 * 比较两个字符串数组是否完全一致。
 *
 * @param left 左侧数组。
 * @param right 右侧数组。
 * @returns 是否一致。
 */
function areStringArraysEqual(left: readonly string[], right: readonly string[]): boolean {
    if (left.length !== right.length) {
        return false;
    }

    return left.every((value, index) => value === right[index]);
}

/**
 * 比较两个特征摘要是否完全一致。
 *
 * @param current 当前特征摘要。
 * @param confirmed 用户确认过的特征摘要。
 * @returns 是否一致。
 */
function areFeatureSummariesEqual(
    current: FeatureDeletionFeatureSummary,
    confirmed: FeatureDeletionFeatureSummary,
): boolean {
    return current.featureId === confirmed.featureId
        && current.featureKind === confirmed.featureKind
        && current.displayName === confirmed.displayName
        && current.commandIndex === confirmed.commandIndex
        && current.sourceSketchId === confirmed.sourceSketchId;
}

/**
 * 比较两个特征摘要数组是否完全一致。
 *
 * @param current 当前特征摘要数组。
 * @param confirmed 用户确认过的特征摘要数组。
 * @returns 是否一致。
 */
function areFeatureSummaryArraysEqual(
    current: readonly FeatureDeletionFeatureSummary[],
    confirmed: readonly FeatureDeletionFeatureSummary[],
): boolean {
    if (current.length !== confirmed.length) {
        return false;
    }

    return current.every((feature, index) => {
        const confirmedFeature = confirmed[index];

        return typeof confirmedFeature !== 'undefined'
            && areFeatureSummariesEqual(feature, confirmedFeature);
    });
}

/**
 * 解析删除计划不一致的原因。
 *
 * @param current 当前即将执行的删除计划。
 * @param confirmed 用户确认过的删除计划。
 * @returns 不一致原因列表。
 */
function resolveFeatureDeletionPlanMismatchReasons(
    current: FeatureDeletionPlan,
    confirmed: FeatureDeletionPlan,
): string[] {
    const reasons: string[] = [];

    if (current.commandSeq !== confirmed.commandSeq) {
        reasons.push('命令历史已变化');
    }
    if (!areFeatureSummariesEqual(current.targetFeature, confirmed.targetFeature)) {
        reasons.push('目标特征已变化');
    }
    if (!areStringArraysEqual(current.deletedFeatureIds, confirmed.deletedFeatureIds)) {
        reasons.push('待删除特征范围已变化');
    }
    if (!areFeatureSummaryArraysEqual(current.childFeatures, confirmed.childFeatures)) {
        reasons.push('关联删除特征列表已变化');
    }
    if (!areStringArraysEqual(current.affectedSketchIds, confirmed.affectedSketchIds)) {
        reasons.push('受影响草图范围已变化');
    }
    if (!areStringArraysEqual(current.restoredSketchIds, confirmed.restoredSketchIds)) {
        reasons.push('恢复草图范围已变化');
    }
    if (!areStringArraysEqual(current.deletedSketchIds, confirmed.deletedSketchIds)) {
        reasons.push('删除草图范围已变化');
    }

    return reasons;
}

/**
 * 校验当前删除计划是否仍等同于用户确认过的计划。
 *
 * 设计说明：
 * 1. 删除确认框展示的是 `confirmed` 的 child features / absorbed sketches；
 * 2. 用户确认后，模型历史可能已被其它队列动作改变，必须在真正 replay 前重新校验；
 * 3. 一旦不一致直接拒绝执行，让上层重新解析并重新弹确认，避免删除用户没有确认的关联项。
 *
 * @param current 当前即将执行的删除计划。
 * @param confirmed 用户确认过的删除计划。
 * @throws 当当前删除计划与用户已经确认的删除计划不一致时。
 */
export function assertFeatureDeletionPlanMatchesConfirmedPlan(
    current: FeatureDeletionPlan,
    confirmed: FeatureDeletionPlan,
): void {
    const reasons = resolveFeatureDeletionPlanMismatchReasons(current, confirmed);
    if (reasons.length === 0) {
        return;
    }

    throw new Error(`${FEATURE_DELETION_PLAN_CHANGED_MESSAGE}${reasons.join('、')}`);
}
